/**
 * University results supporting both Card View and List View.
 *
 * Preserves all existing selectors and data requirements:
 * - .unicard.row-open
 * - .unicard-meta
 * - onOpen(u)
 */
import { useState } from 'react';
import { assetUrl } from '../assetUrl.js';

function initials(name) {
  return (name ?? '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
}

/**
 * The university logo, with an initials monogram as the fallback — used when
 * the record has no `image`, or the image URL 404s (coverage is ~80%).
 */
function Avatar({ src, name, className }) {
  const [broken, setBroken] = useState(false);
  if (src && !broken) {
    return (
      <img
        src={src}
        alt=""
        className={`${className} ava-img`}
        loading="lazy"
        onError={() => setBroken(true)}
      />
    );
  }
  return <span className={className}>{initials(name)}</span>;
}

function FeeFrom({ uni }) {
  if (uni.feeFrom == null) return <span className="muted">On request</span>;
  const amount = uni.feeFrom.toLocaleString();
  return <>{`from ${uni.feeCurrency ?? ''} ${amount}`.trim()}</>;
}

export function UniversityResults({ rows, found, onOpen, onApply, view = 'card' }) {
  if (found === 0) {
    return (
      <div className="empty">
        <b>No universities match all of these filters</b>
        Remove one of the tags above to widen the search.
      </div>
    );
  }

  // A card opens the detail drawer. The whole row is passed: the drawer fetches
  // the MongoDB DTO by `u.id`, and reads a few search-derived admission signals
  // (MOI accepted, academic-gap tolerance, pathway) straight off the row.
  const open = (u) => onOpen?.(u);

  const handleApply = (e, u) => {
    e.stopPropagation();
    if (onApply) {
      onApply(u);
    } else {
      window.alert(`Application started for ${u.name}`);
    }
  };

  const handleOpenClick = (e, u) => {
    e.stopPropagation();
    open(u);
  };

  if (view === 'list') {
    return (
      <div className="uni-list-wrap">
        <table className="results-table uni-table">
          <thead>
            <tr>
              <th>University</th>
              <th>Location</th>
              <th>Course Types</th>
              <th>Intakes</th>
              <th style={{ textAlign: 'right' }}>Tuition / yr</th>
              <th>Admissions</th>
              <th style={{ textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((u) => {
              const locationStr = [u.country, u.cities?.[0]].filter(Boolean).join(' · ');
              const courseTypesStr = (u.courseTypes ?? u.degreeGroups ?? []).slice(0, 3).join(', ');
              const intakesStr = (u.intakes ?? []).slice(0, 3).join(', ');
              const admissionSignals = [
                u.gapYearsMax != null && `gap ≤ ${u.gapYearsMax}y`,
                u.moiAccepted && 'MOI',
                u.interviewRequired && 'interview',
              ].filter(Boolean).join(' · ');

              return (
                <tr
                  key={u.id}
                  className="unicard-row row-open"
                  tabIndex={0}
                  role="button"
                  aria-label={`Open ${u.name}`}
                  onClick={() => open(u)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(u); }
                  }}
                >
                  <td data-label="University">
                    <div className="uni-table-name-cell">
                      <Avatar src={assetUrl(u.image)} name={u.name} className="ava-sm" />
                      <div>
                        <strong className="uni-table-title">{u.name}</strong>
                        {u.website && (
                          <a
                            href={u.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="uni-ext-link"
                            onClick={(e) => e.stopPropagation()}
                            title="Visit website"
                          >
                            ↗
                          </a>
                        )}
                        {u.campuses?.length ? (
                          <div className="uni-table-campuses">
                            Campus: {u.campuses[0].name || u.campuses[0]}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </td>
                  <td data-label="Location">
                    <span className="uni-table-loc">{locationStr || '—'}</span>
                  </td>
                  <td data-label="Course Types">
                    <span className="uni-table-types">{courseTypesStr || '—'}</span>
                  </td>
                  <td data-label="Intakes">
                    <span className="uni-table-intakes">{intakesStr || '—'}</span>
                  </td>
                  <td className="num" data-label="Tuition / yr">
                    <FeeFrom uni={u} />
                  </td>
                  <td data-label="Admissions">
                    <span className="uni-table-admissions">{admissionSignals || '—'}</span>
                  </td>
                  <td data-label="Actions" className="table-actions-cell" onClick={(e) => e.stopPropagation()}>
                    <div className="row-action-btns">
                      <button
                        type="button"
                        className="btn-table-details"
                        onClick={(e) => handleOpenClick(e, u)}
                        aria-label={`View details for ${u.name}`}
                      >
                        DETAILS
                      </button>
                      <button
                        type="button"
                        className="btn-table-apply"
                        onClick={(e) => handleApply(e, u)}
                        aria-label={`Apply for ${u.name}`}
                      >
                        APPLY NOW
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  // Default: Card View matching Crizac production screenshots
  return (
    <div className="unicards">
      {rows.map((u) => (
        <div
          className={onOpen ? 'unicard row-open' : 'unicard'}
          key={u.id}
          tabIndex={onOpen ? 0 : undefined}
          role={onOpen ? 'button' : undefined}
          aria-label={onOpen ? `Open ${u.name}` : undefined}
          onClick={onOpen ? () => open(u) : undefined}
          onKeyDown={onOpen ? (e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(u); }
          } : undefined}
        >
          {/* Header Banner: the university's banner photo over the gradient
              fallback (the gradient shows through if the photo is missing or
              404s), plus a circular logo badge. */}
          <div className="unicard-banner">
            <div className="unicard-banner-bg" />
            {u.bannerImage && (
              <img
                className="unicard-banner-img"
                src={assetUrl(u.bannerImage)}
                alt=""
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <Avatar src={assetUrl(u.image)} name={u.name} className="ava" />
          </div>

          <div className="unicard-body">
            <div className="unicard-h">
              <div className="unicard-title">
                <strong>{u.name}</strong>
                {u.website && (
                  <a
                    href={u.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="uni-ext-link"
                    onClick={(e) => e.stopPropagation()}
                    title="Visit website"
                  >
                    ↗
                  </a>
                )}
              </div>
              <div className="unicard-location">
                {[u.country, u.cities?.[0]].filter(Boolean).join(' · ')}
              </div>
              {u.campuses?.length ? (
                <div className="unicard-campus-line">
                  <span className="k">Campus</span>
                  <span className="v">{u.campuses[0].name || u.campuses[0]}</span>
                </div>
              ) : null}
            </div>

            <div className="unicard-meta">
              <div>
                <span className="k">Course types</span>
                <span className="v">
                  {(u.courseTypes ?? u.degreeGroups ?? []).length
                    ? (u.courseTypes ?? u.degreeGroups).slice(0, 3).join(', ')
                      + ((u.courseTypes ?? u.degreeGroups).length > 3 ? ` +${(u.courseTypes ?? u.degreeGroups).length - 3}` : '')
                    : '—'}
                </span>
              </div>
              <div>
                <span className="k">Intakes</span>
                <span className="v">
                  {u.intakes?.length
                    ? u.intakes.slice(0, 4).join(', ') + (u.intakes.length > 4 ? ` +${u.intakes.length - 4}` : '')
                    : '—'}
                </span>
              </div>
              <div>
                <span className="k">Tuition / yr</span>
                <span className="v"><FeeFrom uni={u} /></span>
              </div>
              <div>
                <span className="k">Admissions</span>
                <span className="v">
                  {[
                    u.gapYearsMax != null && `gap ≤ ${u.gapYearsMax}y`,
                    u.moiAccepted && 'MOI',
                    u.interviewRequired && 'interview',
                  ].filter(Boolean).join(' · ') || '—'}
                </span>
              </div>
            </div>
          </div>

          {/* Action buttons matching Crizac production */}
          <div className="unicard-actions">
            <button
              type="button"
              className="btn-uni-details"
              onClick={(e) => handleOpenClick(e, u)}
              aria-label={`View details for ${u.name}`}
            >
              DETAILS
            </button>
            <button
              type="button"
              className="btn-uni-apply"
              onClick={(e) => handleApply(e, u)}
              aria-label={`Apply to ${u.name}`}
            >
              APPLY NOW
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
